<template>
  <div class="wizard-body columns content-box small-9">
    <div class="header-actions">
      <div>
        <h4>{{ $t('OPPORTUNITY_PIPELINES.CREATE_FLOW.LOSS_REASONS.TITLE') }}</h4>
        <p class="header-subtitle">{{ $t('OPPORTUNITY_PIPELINES.CREATE_FLOW.LOSS_REASONS.DESC') }}</p>
      </div>
      <woot-button color-scheme="success" @click="openCreateModal">
        {{ $t('OPPORTUNITY_PIPELINES.LOSS_REASONS.NEW_BUTTON') }}
      </woot-button>
    </div>

    <empty-state
      v-if="!isLoading && !lossReasons.length"
      :title="$t('OPPORTUNITY_PIPELINES.LOSS_REASONS.EMPTY_TITLE')"
      :message="$t('OPPORTUNITY_PIPELINES.LOSS_REASONS.EMPTY_MESSAGE')"
    />

    <woot-loading-state
      v-if="isLoading"
      :message="$t('OPPORTUNITY_PIPELINES.LOSS_REASONS.LOADING')"
    />

    <div v-if="!isLoading && lossReasons.length" class="reasons-list">
      <div v-for="reason in lossReasons" :key="reason.id" class="reason-card">
        <div>
          <p class="reason-name">{{ reason.name }}</p>
          <p class="reason-description">{{ reason.description || '-' }}</p>
        </div>

        <div class="reason-actions">
          <woot-switch
            :value="reason.status === 'active'"
            @input="toggleStatus(reason, $event)"
          />
          <woot-button
            variant="smooth"
            color-scheme="secondary"
            icon="edit"
            @click="openEditModal(reason)"
          />
        </div>
      </div>
    </div>

    <div class="button-row">
      <woot-button
        type="button"
        color-scheme="secondary"
        @click="goToPrevious"
      >
        {{ $t('OPPORTUNITY_PIPELINES.ACTIONS.BACK') }}
      </woot-button>
      <woot-button
        :disabled="!lossReasons.length"
        @click="goToNext"
      >
        {{ $t('OPPORTUNITY_PIPELINES.CREATE_FLOW.LOSS_REASONS.SUBMIT') }}
      </woot-button>
    </div>

    <woot-modal :show.sync="showReasonModal" :on-close="closeModal">
      <loss-reason-modal
        :reason="selectedReason"
        :is-saving="isSaving"
        @submit="saveReason"
        @close="closeModal"
      />
    </woot-modal>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import EmptyState from '../../../../../components/widgets/EmptyState';
import router from '../../../../index';
import LossReasonModal from './LossReasonModal';
import { patchDraft, readDraft } from './draftState';

export default {
  components: {
    EmptyState,
    LossReasonModal,
  },
  mixins: [alertMixin],
  data() {
    return {
      isLoading: false,
      isSaving: false,
      lossReasons: [],
      showReasonModal: false,
      selectedReason: {},
    };
  },
  computed: {
    pipelineId() {
      return this.$route.params.pipelineId;
    },
    isNewDraftFlow() {
      return this.$route.name === 'settings_opportunity_pipelines_new_loss_reasons';
    },
  },
  methods: {
    async fetchLossReasons() {
      if (this.isNewDraftFlow) {
        const draft = readDraft();
        this.lossReasons = draft.lossReasons || [];
        return;
      }

      this.isLoading = true;
      try {
        const lossReasons = await this.$store.dispatch(
          'opportunityPipelines/getLossReasons',
          this.pipelineId
        );
        this.lossReasons = lossReasons || [];
      } catch (error) {
        this.showAlert(this.$t('OPPORTUNITY_PIPELINES.ERRORS.FETCH_LOSS_REASONS'));
      } finally {
        this.isLoading = false;
      }
    },
    openCreateModal() {
      this.selectedReason = {};
      this.showReasonModal = true;
    },
    openEditModal(reason) {
      this.selectedReason = { ...reason };
      this.showReasonModal = true;
    },
    closeModal() {
      this.showReasonModal = false;
      this.selectedReason = {};
    },
    async saveReason(reason) {
      if (this.isNewDraftFlow) {
        if (this.selectedReason.id) {
          this.lossReasons = this.lossReasons.map(item =>
            item.id === this.selectedReason.id
              ? {
                  ...item,
                  ...reason,
                }
              : item
          );
        } else {
          const nextId =
            this.lossReasons.reduce((max, item) => Math.max(max, item.id || 0), 0) + 1;
          this.lossReasons = [
            ...this.lossReasons,
            {
              ...reason,
              id: nextId,
              status: reason.status || 'active',
            },
          ];
        }

        patchDraft({ lossReasons: [...this.lossReasons] });
        this.closeModal();
        return;
      }

      this.isSaving = true;
      try {
        if (this.selectedReason.id) {
          await this.$store.dispatch('opportunityPipelines/updateLossReason', {
            pipelineId: this.pipelineId,
            lossReasonId: this.selectedReason.id,
            payload: reason,
          });
        } else {
          await this.$store.dispatch('opportunityPipelines/createLossReason', {
            pipelineId: this.pipelineId,
            payload: reason,
          });
        }
        await this.fetchLossReasons();
        this.closeModal();
      } catch (error) {
        this.showAlert(this.$t('OPPORTUNITY_PIPELINES.ERRORS.SAVE_LOSS_REASON'));
      } finally {
        this.isSaving = false;
      }
    },
    async toggleStatus(reason, checked) {
      if (this.isNewDraftFlow) {
        reason.status = checked ? 'active' : 'inactive';
        patchDraft({ lossReasons: [...this.lossReasons] });
        return;
      }

      try {
        await this.$store.dispatch('opportunityPipelines/updateLossReason', {
          pipelineId: this.pipelineId,
          lossReasonId: reason.id,
          payload: { ...reason, status: checked ? 'active' : 'inactive' },
        });
        reason.status = checked ? 'active' : 'inactive';
      } catch (error) {
        this.showAlert(this.$t('OPPORTUNITY_PIPELINES.ERRORS.TOGGLE_LOSS_REASON'));
      }
    },
    goToNext() {
      if (this.isNewDraftFlow) {
        patchDraft({ lossReasons: [...this.lossReasons] });
        router.replace({ name: 'settings_opportunity_pipelines_new_finish' });
        return;
      }

      router.replace({
        name: 'settings_opportunity_pipelines_finish',
        params: { pipelineId: this.pipelineId },
      });
    },
    goToPrevious() {
      if (this.isNewDraftFlow) {
        patchDraft({ lossReasons: [...this.lossReasons] });
        router.replace({ name: 'settings_opportunity_pipelines_new_stages' });
        return;
      }

      router.replace({
        name: 'settings_opportunity_pipelines_stages',
        params: { pipelineId: this.pipelineId },
      });
    },
  },
  mounted() {
    this.fetchLossReasons();
  },
};
</script>

<style lang="scss" scoped>
.header-actions {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--space-small);
}

.header-subtitle {
  color: var(--s-700);
  font-size: var(--font-size-small);
  margin: 0;
}

.reasons-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-small);
  margin-top: var(--space-small);
}

.reason-card {
  align-items: center;
  background: var(--white);
  border: 1px solid var(--color-border-light);
  border-radius: var(--border-radius-medium);
  box-shadow: 0 0.6rem 1.6rem rgba(16, 24, 40, 0.04);
  display: flex;
  justify-content: space-between;
  padding: var(--space-normal);
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
}

.reason-card:hover {
  border-color: var(--s-200);
  box-shadow: 0 1rem 2.2rem rgba(16, 24, 40, 0.08);
}

.reason-name {
  font-weight: var(--font-weight-medium);
  margin: 0;
}

.reason-description {
  color: var(--s-700);
  font-size: var(--font-size-mini);
  margin: 0;
}

.reason-actions {
  align-items: center;
  display: flex;
  gap: var(--space-smaller);
}

.button-row {
  display: flex;
  gap: var(--space-small);
  justify-content: flex-end;
  margin-top: var(--space-small);
}

@media screen and (max-width: 768px) {
  .header-actions {
    flex-direction: column;
    gap: var(--space-small);
  }

  .reason-card {
    align-items: flex-start;
    flex-direction: column;
    gap: var(--space-small);
  }

  .reason-actions {
    justify-content: flex-end;
    width: 100%;
  }
}
</style>
